import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { TowerControl, Truck, Container, Users, CheckCircle, ArrowRight, Phone, Warehouse } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const serviceData = {
  'lorry-crane': {
    name: 'Lorry Crane Rental Singapore',
    shortName: 'Lorry Crane',
    icon: TowerControl,
    heroDesc: 'Professional lorry crane rental services in Singapore. From 25-ton to 100-ton capacity, our fleet handles all your heavy lifting and transportation needs with certified operators and full insurance coverage.',
    capacity: '25 - 100 Ton',
    details: ['25 Ton Lorry Crane', '40 Ton Lorry Crane', '60 Ton Lorry Crane', '80 Ton Lorry Crane', '100 Ton Lorry Crane'],
    features: ['Professional certified operators included', 'Latest safety equipment and rigging', 'Regular fleet maintenance', 'Full insurance coverage', 'Same-day availability for urgent jobs'],
    useCases: ['Construction site lifting operations', 'Heavy machinery installation', 'Structural steel erection', 'HVAC and mechanical equipment placement', 'Prefabricated component installation', 'Emergency recovery and salvage'],
    metaTitle: 'Lorry Crane Rental Singapore | 25-100 Ton | NJK Logistics',
    metaDesc: 'Rent lorry cranes from 25 to 100 ton in Singapore. Professional operators, competitive rates, 24/7 availability. Call 98366186 for a free quote.',
    keywords: 'lorry crane rental Singapore, lorry crane hire, crane service Singapore, heavy lifting Singapore, 25 ton crane, 40 ton crane, 60 ton crane, 100 ton crane rental',
    image: 'https://customer-assets.emergentagent.com/job_193da88a-38a6-4eb7-b1f4-1a973cc5c4bb/artifacts/7gbywntd_image.png'
  },
  'tailgate': {
    name: 'Tailgate Box Lorry Rental Singapore',
    shortName: 'Tailgate Box Lorry',
    icon: Truck,
    heroDesc: 'Reliable tailgate box lorry rental in Singapore. Available in 14ft and 24ft configurations, our tailgate lorries provide efficient cargo transportation with easy loading and unloading capabilities.',
    capacity: '14ft & 24ft',
    details: ['14ft Tailgate Box Lorry', '24ft Tailgate Box Lorry'],
    features: ['Hydraulic tailgate for easy loading/unloading', 'Weather-protected enclosed cargo area', 'Experienced and reliable drivers', 'Flexible scheduling options', 'GPS tracking on all vehicles'],
    useCases: ['Office and home relocation', 'Furniture and appliance delivery', 'Exhibition and event logistics', 'Retail stock replenishment', 'E-commerce fulfillment deliveries', 'Warehouse-to-site transportation'],
    metaTitle: 'Tailgate Box Lorry Rental Singapore | 14ft & 24ft | NJK Logistics',
    metaDesc: 'Tailgate box lorry rental in Singapore. 14ft and 24ft available. Easy loading/unloading, experienced drivers. Call 98366186 for booking.',
    keywords: 'tailgate lorry rental Singapore, box lorry hire, tailgate truck Singapore, cargo transport Singapore, delivery lorry rental, 14ft lorry, 24ft lorry',
    image: 'https://customer-assets.emergentagent.com/job_193da88a-38a6-4eb7-b1f4-1a973cc5c4bb/artifacts/2yo3v1hq_WhatsApp%20Image%202026-01-06%20at%201.47.31%20PM.jpeg'
  },
  'trailer': {
    name: 'Trailer Rental Singapore',
    shortName: 'Trailer',
    icon: Container,
    heroDesc: 'Versatile trailer rental services in Singapore. Our fleet includes flatbed trailers, low-bed trailers, and container trailers suitable for all types of heavy cargo transportation across the island.',
    capacity: 'Various Sizes',
    details: ['Flatbed Trailers', 'Low-bed Trailers', 'Container Trailers'],
    features: ['Heavy load capacity for oversized cargo', 'Long-distance and cross-island capable', 'GPS tracking on all units', '24/7 availability for urgent needs', 'Experienced trailer drivers'],
    useCases: ['Container haulage and transport', 'Heavy machinery transportation', 'Oversized cargo delivery', 'Construction material transport', 'Industrial equipment relocation', 'Port-to-warehouse logistics'],
    metaTitle: 'Trailer Rental Singapore | Flatbed & Container | NJK Logistics',
    metaDesc: 'Trailer rental services in Singapore. Flatbed, low-bed and container trailers for heavy cargo transport. Call 98366186.',
    keywords: 'trailer rental Singapore, flatbed trailer hire, container trailer, low bed trailer Singapore, heavy cargo transport, haulage Singapore',
    image: 'https://customer-assets.emergentagent.com/job_193da88a-38a6-4eb7-b1f4-1a973cc5c4bb/artifacts/c00vrclr_image.png'
  },
  'warehouse-storage': {
    name: 'Warehouse Storage Singapore',
    shortName: 'Warehouse Storage',
    icon: Warehouse,
    heroDesc: 'Secure and flexible warehouse storage solutions in Singapore. Short-term and long-term storage with professional handling services, inventory management, and easy access to your goods.',
    capacity: 'Flexible',
    details: ['Short-term storage', 'Long-term storage', 'Handling services', 'Inventory management'],
    features: ['Secure and monitored facility', 'Professional loading/unloading handling', 'Flexible storage periods', 'Easy access during business hours', 'Monthly stock-taking reports'],
    useCases: ['Excess inventory storage', 'Seasonal stock holding', 'Construction material staging', 'E-commerce warehousing', 'Event equipment storage', 'Business relocation temporary storage'],
    metaTitle: 'Warehouse Storage Singapore | Secure & Flexible | NJK Logistics',
    metaDesc: 'Warehouse storage services in Singapore. Secure facility, flexible terms, handling services included. Call 98366186 for rates.',
    keywords: 'warehouse storage Singapore, warehouse rental, storage services Singapore, goods storage, inventory management, warehouse handling',
    image: 'https://customer-assets.emergentagent.com/job_193da88a-38a6-4eb7-b1f4-1a973cc5c4bb/artifacts/15zfg1g2_WhatsApp%20Image%202026-01-27%20at%203.47.06%20PM.jpeg'
  },
  'manpower': {
    name: 'Manpower Services Singapore',
    shortName: 'Manpower',
    icon: Users,
    heroDesc: 'Skilled and reliable logistics manpower services in Singapore. Our trained professionals handle loading, unloading, rigging, and site supervision for all your operational needs.',
    capacity: 'On-Demand',
    details: ['Loading/Unloading crew', 'Warehouse staff', 'Rigging specialists', 'Site supervisors'],
    features: ['Trained and safety-certified professionals', 'Flexible team sizes on demand', 'Short notice availability', 'Experienced in heavy lifting operations', 'Comprehensive safety gear provided'],
    useCases: ['Construction site support', 'Warehouse operations and stock-taking', 'Event setup and teardown', 'Office and industrial relocation', 'Container stuffing and unstuffing', 'Specialized rigging operations'],
    metaTitle: 'Logistics Manpower Services Singapore | NJK Logistics',
    metaDesc: 'Professional logistics manpower in Singapore. Trained crew for loading, unloading, rigging. Flexible team sizes. Call 98366186.',
    keywords: 'manpower services Singapore, logistics manpower, loading unloading crew, rigging specialists, warehouse workers Singapore, labour supply logistics',
    image: 'https://customer-assets.emergentagent.com/job_193da88a-38a6-4eb7-b1f4-1a973cc5c4bb/artifacts/cgsbbfqb_WhatsApp%20Image%202026-02-02%20at%2011.01.59%20AM.jpeg'
  }
};

export const ServiceDetail = () => {
  const { slug } = useParams();
  const service = serviceData[slug];

  if (!service) return <Navigate to="/services" replace />;

  const Icon = service.icon;

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="pt-40 pb-16 px-4 bg-gradient-to-br from-stone-100 via-white to-stone-50">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Link to="/services" className="text-burgundy hover:underline text-sm">Services</Link>
                <span className="text-gray-400">/</span>
                <span className="text-sm text-gray-600">{service.shortName}</span>
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6" data-testid="service-detail-title">
                {service.name}
              </h1>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">{service.heroDesc}</p>
              <div className="bg-stone-50 p-4 rounded-lg inline-block mb-6">
                <p className="text-sm font-semibold text-gray-700">Capacity</p>
                <p className="text-2xl font-bold text-burgundy">{service.capacity}</p>
              </div>
              <div className="flex flex-wrap gap-4">
                <Link to="/booking">
                  <Button size="lg" className="bg-burgundy hover:bg-burgundy-light text-white" data-testid="service-book-btn">
                    Book Now <ArrowRight className="ml-2" size={20} />
                  </Button>
                </Link>
                <a href="https://wa.me/6598366186">
                  <Button size="lg" variant="outline" className="border-burgundy text-burgundy hover:bg-stone-100">
                    <Phone className="mr-2" size={18} /> WhatsApp Us
                  </Button>
                </a>
              </div>
            </div>
            {service.image && (
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-xl">
                <img src={service.image} alt={service.name} className="w-full h-full object-cover" />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Options & Features */}
      <section className="py-16 px-4">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Available Options</h2>
              <div className="space-y-3">
                {service.details.map((detail, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg">
                    <CheckCircle className="text-burgundy flex-shrink-0" size={20} />
                    <span className="font-medium text-gray-800">{detail}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Why Choose Our {service.shortName}</h2>
              <div className="space-y-3">
                {service.features.map((feature, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                    <CheckCircle className="text-green-600 flex-shrink-0" size={20} />
                    <span className="text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="container mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Common Applications</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {service.useCases.map((useCase, i) => (
              <Card key={i}>
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-8 h-8 bg-gold/15 rounded-full flex items-center justify-center flex-shrink-0">
                    <Icon className="text-burgundy" size={16} />
                  </div>
                  <span className="text-sm text-gray-700">{useCase}</span>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 bg-burgundy">
        <div className="container mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Need {service.shortName} Services?</h2>
          <p className="text-white/70 text-lg mb-8 max-w-xl mx-auto">
            Get a free quotation today. Our team is ready to help with your logistics needs.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/booking">
              <Button size="lg" className="bg-white text-burgundy hover:bg-gray-100">Book Online</Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-burgundy-light">Get a Quote</Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;
